/* ---------------------------------------------------------------------------
   "Edit details" — the dialog that lets an engineer correct or fill in an
   inventory record from the detail screen, rather than only ever transcribing
   one from a paper sheet at seed time.

   One form covering every editable field, grouped into the same sections the
   detail screen itself uses (src/data/editFields.ts is the shared config), so
   the shape of the dialog matches the shape of what it edits. Values are held
   as draft strings in the store and only parsed back into numbers/nulls on
   submit — see buildEditPayload — so a half-typed number never crashes the
   render on every keystroke.

   Rendered into src/main.ts's dialog layer next to the template picker; see the
   note at the top of that file for why a modal like this needs no lifecycle of
   its own, and paintScope.ts for why the layer is not inside the shell.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, roleCan, type State } from '../core/store.ts';
import { button, field, scrim, tag } from '../components/widgets.ts';
import { EDIT_SECTIONS, type EditField } from '../data/editFields.ts';
import { allBridges, findBridge } from '../data/derive.ts';

/**
 * Whether a change to the draft changes anything this dialog draws other than the
 * contents of the input the change came from. Nothing here is derived from a draft
 * value — every field is drawn by its own input, and the errors and the saving
 * state are separate — so typing never needs a render. See src/main.ts. The one
 * exception is a form filled from a model (LC-83): the mark beside a filled value
 * goes when it is typed over.
 */
export function editDialogNeedsPaint(s: Pick<State, 'editFill'>): boolean {
  return s.editFill !== null;
}

/**
 * The three states a `bool` field can be in.
 *
 * The blank one is first and is the default, so a record nobody has assessed
 * stays unassessed rather than acquiring a "No" the moment somebody opens the
 * form to correct a chainage.
 */
const BOOL_OPTIONS = [
  { value: '', label: 'Not recorded' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
] as const;

/**
 * An input the model filled (LC-83), marked as the model's. The mark is the
 * form's; the change log's source is decided by the server on the save.
 */
function fromModel(fill: State['editFill'], key: string, value: string, input: HTMLElement): HTMLElement {
  const filled = fill?.values[key];
  if (filled === undefined) return input;
  return h(
    'span',
    { class: 'dlg__filled', 'data-from-model': key },
    input,
    value === filled ? tag('M', 1, `As model ${fill!.modelId} states it`) : null,
  );
}

/** The option that swaps a dropdown for a text input. Not a value any field can hold. */
const OTHER = ' other';

/** A field's dropdown values: its own list, or what the register already holds for it. */
function optionsOf(f: EditField): readonly string[] {
  if (f.options) return f.options;
  if (!f.optionsFrom) return [];
  const key = f.optionsFrom;
  const held = new Set<string>();
  for (const b of allBridges()) {
    const v = b[key];
    if (typeof v === 'string' && v.trim() !== '') held.add(v.trim());
  }
  return [...held].sort((a, b) => a.localeCompare(b));
}

/**
 * A field with the usual values as a dropdown. The value already on the record
 * is always one of its options, listed or not, so opening the form and saving it
 * never blanks a description the list does not carry. "Other…" types one in.
 */
function picker(s: Readonly<State>, f: EditField, id: string): HTMLElement {
  const value = s.editDraft[f.key] ?? '';
  if (s.editOther.includes(f.key)) {
    return h(
      'span',
      { class: 'dlg__other' },
      h('input', {
        id,
        class: 'input',
        type: 'text',
        required: f.required || undefined,
        placeholder: 'Type a value',
        value,
        oninput: (e: Event) => actions.setEditField(f.key, (e.target as HTMLInputElement).value),
      }),
      h(
        'button',
        {
          type: 'button',
          class: 'dlg__other-back',
          title: 'Choose from the list',
          'aria-label': 'Choose from the list',
          onclick: () => actions.setEditOther(f.key, false),
        },
        icon(ICONS.list, { size: 14, stroke: 'currentColor' }),
      ),
    );
  }
  const listed = optionsOf(f);
  const extra = value !== '' && !listed.includes(value) ? [value] : [];
  return h(
    'select',
    {
      id,
      class: 'select',
      required: f.required || undefined,
      onchange: (e: Event) => {
        const v = (e.target as HTMLSelectElement).value;
        if (v !== OTHER) return actions.setEditField(f.key, v);
        actions.setEditOther(f.key, true);
        requestAnimationFrame(() => document.getElementById(id)?.focus());
      },
    },
    h('option', { value: '', selected: value === '', text: f.required ? 'Choose…' : 'Not recorded' }),
    ...[...extra, ...listed].map((opt) => h('option', { value: opt, selected: opt === value, text: opt })),
    h('option', { value: OTHER, text: 'Other…' }),
  );
}

/** The dialog, or null when it is closed. */
export function editDialog(s: Readonly<State>): HTMLElement | null {
  if (s.editingId === null) return null;
  /* New bridge is this form on a blank record, with the asset id added above
     it — see `openNewBridge`. Everything below the id is the same fields, held
     to the same rules, as correcting a record that exists. */
  const creating = s.editCreating;
  const b = creating ? null : findBridge(s.editingId);
  /* No record to hang this on, which happens only where the register is
     empty. Closed rather than drawn over nothing — the same answer this
     dialog already gives when the thing it was opened for has gone. */
  if (!creating && b === null) return null;
  const title = b ? `Edit ${b.name}` : 'New bridge';
  /* *Fill from model* (LC-83), on any structure with a model, handed over or not. */
  const hasModel = b !== null && (b.modelOfRecord != null || (s.selectedId === b.id && s.models.models.length > 0));
  const mayFill = hasModel && roleCan(s, 'inventory.edit') && s.backend === 'live';
  const fill = s.editFill;

  return h(
    'div',
    {
      class: 'dlg',
      ...scrim(() => actions.closeEdit(), () => s.editSaving),
    },
    h(
      'form',
      {
        class: 'dlg__panel',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': title,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveEdit();
        },
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', {
            class: 'dlg__kicker',
            text: b ? `${b.id} · inventory record` : 'Register · new structure',
          }),
          h('h2', { class: 'dlg__title', text: title }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Cancel',
            'aria-label': 'Cancel',
            onclick: () => actions.closeEdit(),
          },
          '×',
        ),
      ),

      s.editError
        ? h(
            'div',
            { class: 'dlg__note dlg__note--error' },
            h('span', { text: s.editError }),
          )
        : null,
      fill
        ? h(
            'div',
            { class: 'dlg__note', 'data-filled-from': fill.modelId },
            h('span', { text: [`Filled from model ${fill.modelId}.`, ...fill.notes].join(' ') }),
          )
        : null,

      h(
        'div',
        { class: 'dlg__body' },
        creating
          ? h(
              'fieldset',
              { class: 'dlg__section' },
              h('legend', { class: 'dlg__section-title', text: 'Asset' }),
              h(
                'div',
                { class: 'dlg__form-grid' },
                /* What is being added (LC-10): a structure already standing, which
                   is an inventory record, or one to be built, which starts its
                   life as planned. Offered as the roles allow. */
                creating && roleCan(s, 'inventory.edit') && roleCan(s, 'structure.create')
                  ? field(
                      'Stage',
                      h(
                        'select',
                        {
                          id: 'edit-phase',
                          class: 'select',
                          onchange: (e: Event) => actions.setEditField('phase', (e.target as HTMLSelectElement).value),
                        },
                        h('option', { value: '', selected: (s.editDraft['phase'] ?? '') === '', text: 'Standing structure' }),
                        h('option', { value: 'planned', selected: s.editDraft['phase'] === 'planned', text: 'Planned structure' }),
                      ),
                      'edit-phase',
                      'Whether the structure already stands (an inventory record) or is still to be built (a planned structure).',
                    )
                  : null,
                field(
                  'Asset id',
                  h('input', {
                    id: 'edit-id',
                    class: 'input',
                    type: 'text',
                    required: true,
                    placeholder: 'e.g. PK-M1-0200',
                    autocapitalize: 'characters',
                    value: s.editDraft['id'] ?? '',
                    oninput: (e: Event) =>
                      actions.setEditField('id', (e.target as HTMLInputElement).value),
                  }),
                  'edit-id',
                  'The register’s unique id for the structure, e.g. PK-M1-0200. It cannot be changed once created.',
                ),
              ),
            )
          : null,
        ...EDIT_SECTIONS.map((section) =>
          h(
            'fieldset',
            { class: 'dlg__section' },
            h('legend', { class: 'dlg__section-title', text: section.title }),
            h(
              'div',
              { class: 'dlg__form-grid' },
              ...section.fields.filter((f) => !(creating && f.modelled)).map((f) =>
                field(
                  f.label,
                  /* A three-state select, not a checkbox. A checkbox can only say
                     yes or no, and the register has to be able to say that nobody
                     has looked — see FieldKind in src/data/editFields.ts. */
                  f.kind === 'bool'
                    ? h(
                        'select',
                        {
                          id: `edit-${f.key}`,
                          class: 'select',
                          onchange: (e: Event) =>
                            actions.setEditField(f.key, (e.target as HTMLSelectElement).value),
                        },
                        ...BOOL_OPTIONS.map((opt) =>
                          h('option', {
                            value: opt.value,
                            selected: (s.editDraft[f.key] ?? '') === opt.value,
                            text: opt.label,
                          }),
                        ),
                      )
                    : fromModel(
                        fill,
                        f.key,
                        s.editDraft[f.key] ?? '',
                        f.options || f.optionsFrom ? picker(s, f, `edit-${f.key}`) : h('input', {
                          // Focus and caret are restored across a re-render by id — see
                          // preserveFocus in src/main.ts — so a field without one loses
                          // both on every keystroke.
                          id: `edit-${f.key}`,
                          class: 'input',
                          type: f.kind === 'text' ? 'text' : 'number',
                          step: f.kind === 'float' ? 'any' : '1',
                          required: f.required || undefined,
                          value: s.editDraft[f.key] ?? '',
                          oninput: (e: Event) =>
                            actions.setEditField(f.key, (e.target as HTMLInputElement).value),
                        }),
                      ),
                  `edit-${f.key}`,
                  f.hint,
                ),
              ),
            ),
          ),
        ),
      ),

      h(
        'div',
        { class: 'dlg__foot' },
        mayFill && !creating
          ? button('Fill from model', {
              iconPath: ICONS.cube,
              disabled: s.editSaving,
              title: 'Fill superstructure, spans, lengths, deck width, bearings and foundations from the model of record, else the newest model; each stays editable',
              onClick: () => void actions.fillEditFromModel(),
            })
          : null,
        h('span', { class: 'spacer' }),
        button('Cancel', { onClick: () => actions.closeEdit(), disabled: s.editSaving }),
        button(s.editSaving ? 'Saving…' : creating ? 'Create structure' : 'Save changes', {
          type: 'submit',
          primary: true,
          disabled: s.editSaving,
        }),
      ),
    ),
  );
}
